// item 617 — the RNWY three-pricing-options structured builder shape + compute helpers.
// Mirrors the backend basix.da/Models/RnwyPricing.cs exactly (camelCase JSON on the wire).
// The physical work (config + hardware) is SHARED across all three options; the options
// differ only in licensing (perpetual one-time vs subscription monthly), the end-of-term
// perpetual buy-in (subscriptions), and the Help Desk tier.

export interface RnwyLine {
  label: string;
  amount: number;
  note?: string;
}

export interface RnwyHardwareLine {
  label: string;
  sku?: string;
  quantity: number;
  unitAmount: number;
}

export interface RnwyOption {
  licenseOneTime?: number | null; // perpetual only
  licenseMonthly?: number | null; // subscription only
  retainAccessBuyIn?: number | null; // subscription only (end-of-term perpetual buy-in)
  faq?: string;
  // item 620 — Help Desk is NO LONGER per-option; it's a priced menu from Settings, chosen by
  // the client at approval. (Older saved JSON may still carry helpDeskTier/helpDeskMonthly; they
  // are simply ignored.)
}

export interface RnwyPricing {
  configuration: RnwyLine[];
  hardware: RnwyHardwareLine[];
  perpetual: RnwyOption;
  twoYear: RnwyOption;
  fiveYear: RnwyOption;
}

// Help Desk tiers.
export const HELP_DESK_TIERS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Gold' },
  { value: 2, label: 'Platinum' },
  { value: 3, label: 'Titanium' },
] as const;

export const tierLabel = (tier: number): string => HELP_DESK_TIERS.find((t) => t.value === tier)?.label ?? 'None';

// Per-option Help Desk availability rules (enforced in the builder UI + at approval):
//  Perpetual — all tiers allowed, optional.
//  2-year    — Gold N/A; Platinum/Titanium; optional (recommended).
//  5-year    — Gold N/A; Platinum/Titanium; REQUIRED.
export type RnwyOptionKey = 'perpetual' | 'twoYear' | 'fiveYear';

export const allowedTiers = (key: RnwyOptionKey): number[] => {
  if (key === 'perpetual') return [0, 1, 2, 3];
  // subscriptions: no Gold
  return key === 'fiveYear' ? [2, 3] : [0, 2, 3];
};

export const helpDeskRequired = (key: RnwyOptionKey): boolean => key === 'fiveYear';

const n = (v: number | null | undefined): number => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
const round2 = (v: number): number => Math.round((v + Number.EPSILON) * 100) / 100;

export const emptyOption = (): RnwyOption => ({
  licenseOneTime: null,
  licenseMonthly: null,
  retainAccessBuyIn: null,
  faq: '',
});

export const emptyPricing = (): RnwyPricing => ({
  configuration: [],
  hardware: [],
  perpetual: emptyOption(),
  twoYear: emptyOption(),
  fiveYear: emptyOption(),
});

// tolerant parse: bad/blank JSON → null (caller falls back to the flat inputs / empty builder)
export const parsePricing = (json: string | null | undefined): RnwyPricing | null => {
  if (!json || !json.trim()) return null;
  try {
    const raw = JSON.parse(json) as Partial<RnwyPricing>;
    return {
      configuration: Array.isArray(raw.configuration) ? raw.configuration : [],
      hardware: Array.isArray(raw.hardware) ? raw.hardware : [],
      perpetual: { ...emptyOption(), ...(raw.perpetual ?? {}) },
      twoYear: { ...emptyOption(), ...(raw.twoYear ?? {}) },
      fiveYear: { ...emptyOption(), ...(raw.fiveYear ?? {}) },
    };
  } catch {
    return null;
  }
};

export const sharedOneTime = (p: RnwyPricing): number => {
  const config = (p.configuration ?? []).reduce((s, l) => s + n(l.amount), 0);
  const hardware = (p.hardware ?? []).reduce((s, l) => s + n(l.quantity) * n(l.unitAmount), 0);
  return round2(config + hardware);
};

// Per-option computed totals (matches the C# roll-ups + the PDF renderer).
export const perpetualOneTime = (p: RnwyPricing): number => round2(sharedOneTime(p) + n(p.perpetual.licenseOneTime));

// item 620 — the monthly figure is the LICENSE only; Help Desk is a separate priced add-on.
export const twoYearMonthly = (p: RnwyPricing): number => round2(n(p.twoYear.licenseMonthly));

export const fiveYearMonthly = (p: RnwyPricing): number => round2(n(p.fiveYear.licenseMonthly));

// The one-time figure per option: perpetual adds its license, subscriptions are shared-only.
export const optionOneTime = (p: RnwyPricing, key: RnwyOptionKey): number => (key === 'perpetual' ? perpetualOneTime(p) : sharedOneTime(p));

// The monthly license figure per option (perpetual has none; subscriptions bill monthly).
export const optionMonthly = (p: RnwyPricing, key: RnwyOptionKey): number => {
  if (key === 'perpetual') return 0;
  return key === 'twoYear' ? twoYearMonthly(p) : fiveYearMonthly(p);
};

// 50/50 split of a one-time figure (deposit rounds; final is the remainder).
export const split5050 = (oneTime: number): { deposit: number; final: number } => {
  const deposit = round2(oneTime / 2);
  return { deposit, final: round2(oneTime - deposit) };
};

export const money = (v: number): string => v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
